import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api.js';
import { useCallStore, CALL_STATES } from '../store/callStore.js';
import { useNotifyStore } from '../store/notifyStore.js';
import { useWebSocket } from '../hooks/useWebSocket.js';
import { useMedia } from '../hooks/useMedia.js';
import { useWebRTC } from '../hooks/useWebRTC.js';
import VideoWindow from '../components/VideoWindow.jsx';
import CallControls from '../components/CallControls.jsx';
import ErrorView from '../components/ErrorView.jsx';
import {
  CopyIcon,
  CheckIcon,
  RecordIcon,
  StopRecordIcon,
  TrashIcon,
  UsersIcon,
  MaximizeIcon,
  MinimizeIcon,
  VideoIcon,
  MicIcon,
  VideoOffIcon,
  RefreshIcon,
  SovcombankLogo,
} from '../components/Icons.jsx';
import { CallRecorder } from '../utils/callRecorder.js';

const ROLE_LABELS = { operator: 'Оператор Совкомбанка', client: 'Клиент' };
const SESSION_ID_KEY = 'video-callback.session-id';

function sessionId() {
  let sid = localStorage.getItem(SESSION_ID_KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem(SESSION_ID_KEY, sid);
  }
  return sid;
}

/** @param {string} id */
function shortId(id) {
  return id.length > 12 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
}

/**
 * Format elapsed seconds into mm:ss
 * @param {number} sec
 */
function formatDuration(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * CallView - Active video call screen.
 * Optimized for mobile viewports, touch targets, In-App PiP, and Native PiP.
 * @param {object} props
 * @param {string} props.roomId
 * @param {'operator'|'client'} props.role
 */
function CallView({ roomId, role }) {
  const navigate = useNavigate();
  const notify = useNotifyStore((s) => s.notify);

  const state = useCallStore((s) => s.state);
  const localStream = useCallStore((s) => s.localStream);
  const deviceReady = useCallStore((s) => s.deviceReady);
  const remoteStreams = useCallStore((s) => s.remoteStreams);
  const error = useCallStore((s) => s.error);
  const startAcquiring = useCallStore((s) => s.startAcquiring);
  const setConnecting = useCallStore((s) => s.setConnecting);
  const setConnected = useCallStore((s) => s.setConnected);
  const setDeviceReady = useCallStore((s) => s.setDeviceReady);
  const setError = useCallStore((s) => s.setError);
  const addRemoteStream = useCallStore((s) => s.addRemoteStream);
  const removeRemoteStream = useCallStore((s) => s.removeRemoteStream);
  const endCall = useCallStore((s) => s.endCall);
  const reset = useCallStore((s) => s.reset);

  const [wsUrl, setWsUrl] = useState(/** @type {string|null} */ (null));
  const [muted, setMuted] = useState(false);
  const [videoOn, setVideoOn] = useState(true);
  const [recording, setRecording] = useState(false);
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const recorderRef = useRef(/** @type {CallRecorder|null} */ (null));
  const [participants, setParticipants] = useState(/** @type {Map<string, {name: string; role: string}>} */ (new Map()));
  const [remoteProducerIds, setRemoteProducerIds] = useState(/** @type {Set<string>} */ (new Set()));
  const producerUserMapRef = useRef(/** @type {Map<string, string>} */ (new Map()));
  const [deleting, setDeleting] = useState(false);

  // UI state
  const [layoutMode, setLayoutMode] = useState(/** @type {'stage'|'grid'} */ ('stage'));
  const [swappedStreams, setSwappedStreams] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [isPipActive, setIsPipActive] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Ref to the primary remote video element for Native PiP
  const remoteVideoRef = useRef(/** @type {HTMLVideoElement|null} */ (null));

  // Call timer
  useEffect(() => {
    const start = Date.now();
    const timer = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Listen for native PiP state changes
  useEffect(() => {
    const onPipChange = () => {
      setIsPipActive(Boolean(document.pictureInPictureElement));
    };
    document.addEventListener('enterpictureinpicture', onPipChange);
    document.addEventListener('leavepictureinpicture', onPipChange);
    return () => {
      document.removeEventListener('enterpictureinpicture', onPipChange);
      document.removeEventListener('leavepictureinpicture', onPipChange);
    };
  }, []);

  // Listen for fullscreen change
  useEffect(() => {
    const onFs = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // Join the room via HTTP join endpoint on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const opts = role === 'client'
          ? { name: 'Клиент', sessionId: sessionId() }
          : { sessionId: sessionId() };
        const result = await (role === 'client' ? api.clientJoin(roomId, opts) : api.operatorJoin(roomId, opts));
        if (!cancelled) {
          setWsUrl(result.ws_url);
          startAcquiring(result.room);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          notify('error', err.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [roomId, role, startAcquiring, setError, notify]);

  const mediaEnabledRef = useRef(true);

  const handleMediaError = useCallback((err) => {
    notify('warning', `Камера/микрофон недоступны: ${err.message}`);
  }, [notify]);

  const { stream, facingMode, switchCamera, hasMultipleCameras } = useMedia(mediaEnabledRef.current, handleMediaError);

  useEffect(() => {
    if (stream && localStream !== stream) {
      setConnected(stream);
    }
  }, [stream, localStream, setConnected]);

  // Track remote producers from signaling messages
  const onSignaling = useCallback((type, payload) => {
    console.log('[ROOM] signaling event:', type, JSON.stringify(payload).slice(0, 200));
    switch (type) {
      case 'participant': {
        const { userId, role: r, name } = payload;
        setParticipants((prev) => {
          const next = new Map(prev);
          next.set(userId, { name: name || userId.slice(0, 8), role: r });
          return next;
        });
        break;
      }
      case 'left': {
        const { userId } = payload;
        if (userId) {
          removeRemoteStream(userId);
          setParticipants((prev) => {
            const next = new Map(prev);
            next.delete(userId);
            return next;
          });
        }
        break;
      }
      case 'newProducer': {
        const { producerId, kind, userId } = payload;
        if (producerId) {
          if (userId) producerUserMapRef.current.set(producerId, userId);
          setRemoteProducerIds((prev) => {
            const next = new Set(prev);
            next.add(producerId);
            return next;
          });
        }
        break;
      }
      case 'producers': {
        const list = payload.producers || [];
        if (list.length) {
          setRemoteProducerIds((prev) => {
            const next = new Set(prev);
            for (const p of list) {
              if (p && p.producerId) {
                if (p.userId) producerUserMapRef.current.set(p.producerId, p.userId);
                next.add(p.producerId);
              }
            }
            return next;
          });
        }
        break;
      }
      case 'consumed': {
        const { producerId, userId } = payload;
        if (producerId) {
          if (userId) producerUserMapRef.current.set(producerId, userId);
          setRemoteProducerIds((prev) => {
            const next = new Set(prev);
            next.add(producerId);
            return next;
          });
        }
        break;
      }
      case 'subscribed': {
        const { consumerId, producerId, track, kind, userId } = payload;
        if (track) {
          const peerId = userId || producerUserMapRef.current.get(producerId) || producerId;
          const currentStreams = useCallStore.getState().remoteStreams;
          const existing = currentStreams.get(peerId);
          const s = new MediaStream(existing ? existing.getTracks() : []);
          const oldTrack = s.getTracks().find((t) => t.kind === track.kind);
          if (oldTrack) s.removeTrack(oldTrack);
          s.addTrack(track);
          addRemoteStream(peerId, s);
        }
        break;
      }
      case 'deviceReady': {
        setDeviceReady(true);
        break;
      }
      case 'producersCleared': {
        const ids = payload.producerIds || [];
        for (const id of ids) {
          const uId = producerUserMapRef.current.get(id);
          if (uId) removeRemoteStream(uId);
          removeRemoteStream(id);
          subscribedRef.current.delete(id);
        }
        setRemoteProducerIds((prev) => {
          const next = new Set(prev);
          for (const id of ids) next.delete(id);
          return next;
        });
        break;
      }
      case 'producerGone': {
        const id = payload.producerId;
        if (id) {
          const uId = producerUserMapRef.current.get(id);
          if (uId) removeRemoteStream(uId);
          removeRemoteStream(id);
          subscribedRef.current.delete(id);
          setRemoteProducerIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
        break;
      }
      default:
        break;
    }
  }, [setDeviceReady, addRemoteStream, removeRemoteStream]);

  const onSignalingRef = useRef(onSignaling);
  useEffect(() => { onSignalingRef.current = onSignaling; }, [onSignaling]);

  const signalRef = useRef(null);

  const { send, connected: wsConnected } = useWebSocket({
    wsUrl,
    onMessage: (msg) => {
      if (typeof msg?.type === 'string') {
        const payload = msg.payload || msg.data || {};
        if (msg.from && typeof payload === 'object' && !payload.userId) {
          payload.userId = msg.from;
        }
        if (signalRef.current) {
          signalRef.current(msg);
        }
        if (onSignalingRef.current) {
          onSignalingRef.current(msg.type, payload);
        }
      }
    },
    onError: (err) => {
      notify('error', `Соединение с сервером потеряно: ${err.message}`);
    },
  });

  const wsSend = useCallback((msg) => {
    if (typeof msg?.type === 'string') {
      send(msg.type, msg.payload);
    }
  }, [send]);

  const { subscribeToProducer, signal } = useWebRTC({
    localStream,
    send: wsSend,
    onSignaling,
    onReady: () => {},
    wsConnected,
  });
  signalRef.current = signal;

  const subscribedRef = useRef(new Set());

  // Subscribe to remote producers
  useEffect(() => {
    if (!deviceReady) return;
    for (const id of remoteProducerIds) {
      if (subscribedRef.current.has(id)) continue;
      subscribedRef.current.add(id);
      const userId = producerUserMapRef.current.get(id);
      subscribeToProducer(id, userId).catch((err) => {
        console.error('[call] subscribe failed', err);
        setRemoteProducerIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        subscribedRef.current.delete(id);
      });
    }
  }, [deviceReady, remoteProducerIds, subscribeToProducer]);

  useEffect(() => {
    if (wsUrl) setConnecting();
  }, [wsUrl, setConnecting]);

  const remoteArray = Array.from(remoteStreams.entries());
  const hasRemote = remoteArray.length > 0;
  const firstRemote = hasRemote ? remoteArray[0] : null;
  const remotePeerId = firstRemote ? firstRemote[0] : null;
  const remoteStream = firstRemote ? firstRemote[1] : null;
  const remoteParticipant = remotePeerId ? participants.get(remotePeerId) : null;
  const remoteName = remoteParticipant?.name || (role === 'operator' ? 'Клиент' : 'Оператор');

  // Keep active recorder streams updated dynamically
  useEffect(() => {
    if (recorderRef.current && recording) {
      recorderRef.current.updateStreams({
        localStream,
        remoteStream,
        localName: role === 'operator' ? 'Оператор' : 'Клиент',
        remoteName: role === 'operator' ? remoteName : 'Оператор',
      });
    }
  }, [localStream, remoteStream, recording, role, remoteName]);

  // Clean up recorder on unmount
  useEffect(() => {
    return () => {
      if (recorderRef.current) {
        recorderRef.current.cleanup();
        recorderRef.current = null;
      }
    };
  }, []);

  const handleMic = useCallback((mutedState) => {
    setMuted(mutedState);
    if (localStream) {
      localStream.getAudioTracks().forEach((t) => { t.enabled = !mutedState; });
    }
  }, [localStream]);

  const handleVideo = useCallback((on) => {
    setVideoOn(on);
    mediaEnabledRef.current = on;
    if (localStream) {
      localStream.getVideoTracks().forEach((t) => { t.enabled = on; });
    }
  }, [localStream]);

  const stopAndUploadRecording = useCallback(async () => {
    if (!recorderRef.current) return;
    setRecording(false);
    setUploadingRecording(true);
    notify('info', 'Остановка записи и обработка на сервере…');
    try {
      // 1. Notify backend
      await api.stopRecording(roomId).catch(() => {});

      // 2. Stop recorder and get video blob
      const blob = await recorderRef.current.stop();
      recorderRef.current = null;

      if (blob && blob.size > 0) {
        notify('info', `Загрузка записи (${(blob.size / 1048576).toFixed(1)} МБ)…`);
        const saved = await api.uploadRecording(roomId, blob, `recording_${roomId}.webm`);
        notify('success', 'Запись видеозвонка сохранена на бэкенде!');
        console.log('Recording uploaded successfully:', saved);
      } else {
        notify('warning', 'Запись не содержит данных или была прервана');
      }
    } catch (err) {
      console.error('Failed to upload recording:', err);
      notify('error', `Ошибка сохранения записи: ${err.message}`);
    } finally {
      setUploadingRecording(false);
    }
  }, [roomId, notify]);

  const handleLeave = useCallback(async () => {
    try {
      if (recording && recorderRef.current) {
        await stopAndUploadRecording();
      }
      if (role === 'operator') {
        await api.endCall(roomId);
        notify('success', 'Звонок завершён');
      } else {
        await api.getRoom(roomId).catch(() => {});
        notify('info', 'Вы покинули звонок');
      }
    } catch (err) {
      notify('error', `Не удалось завершить: ${err.message}`);
    } finally {
      endCall();
      reset();
      navigate('/rooms');
    }
  }, [recording, stopAndUploadRecording, roomId, role, notify, endCall, reset, navigate]);

  const handleRecording = useCallback(async () => {
    try {
      if (recording) {
        await stopAndUploadRecording();
      } else {
        await api.startRecording(roomId).catch(() => {});
        const rec = new CallRecorder();
        rec.start({
          localStream,
          remoteStream,
          localName: role === 'operator' ? 'Оператор' : 'Клиент',
          remoteName: role === 'operator' ? remoteName : 'Оператор',
        });
        recorderRef.current = rec;
        setRecording(true);
        notify('success', 'Запись видеозвонка начата');
      }
    } catch (err) {
      notify('error', `Ошибка записи: ${err.message}`);
    }
  }, [recording, roomId, localStream, remoteStream, role, remoteName, notify, stopAndUploadRecording]);

  const handleDeleteRoom = useCallback(async () => {
    if (!confirm('Удалить эту комнату? Запись и все данные будут потеряны.')) return;
    setDeleting(true);
    try {
      await api.deleteRoom(roomId);
      notify('success', 'Комната удалена');
      endCall();
      reset();
      navigate('/rooms');
    } catch (err) {
      notify('error', `Не удалось удалить комнату: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  }, [roomId, endCall, reset, navigate, notify]);

  // Native Picture-in-Picture Toggle
  const isPipSupported = typeof document !== 'undefined' &&
    (Boolean(document.pictureInPictureEnabled) || 'webkitSetPresentationMode' in document.createElement('video'));

  const handleTogglePip = useCallback(async () => {
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPipActive(false);
        return;
      }
      const el = remoteVideoRef.current;
      if (el && typeof el.requestPictureInPicture === 'function') {
        await el.requestPictureInPicture();
        setIsPipActive(true);
      } else if (el && typeof el.webkitSetPresentationMode === 'function') {
        el.webkitSetPresentationMode('picture-in-picture');
        setIsPipActive(true);
      } else {
        notify('warning', 'Режим «Картинка в картинке» недоступен на данном видео');
      }
    } catch (err) {
      console.warn('PiP error:', err);
      notify('warning', `PiP недоступен: ${err.message}`);
    }
  }, [notify]);

  // Fullscreen toggle
  const handleToggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  }, []);

  // Copy Room Link
  const handleCopyLink = useCallback(() => {
    const url = `${window.location.origin}/#/room/${roomId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      notify('success', 'Ссылка на комнату скопирована');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      notify('info', url);
    });
  }, [roomId, notify]);

  if (state === CALL_STATES.ACQUIRING && !wsUrl) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-white bg-gray-950">
        <div className="w-16 h-16 rounded-full border-2 border-primary-500/20 border-t-primary-500 animate-spin-slow mb-6" />
        <h3 className="text-lg font-semibold mb-2">Подключение к комнате…</h3>
        <p className="text-sm text-gray-400 max-w-xs text-center">
          Устанавливаем безопасное соединение с сервером
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorView
        title="Ошибка подключения"
        detail={error}
        onRetry={() => {
          setError(null);
          notify('info', 'Повторная попытка…');
        }}
      />
    );
  }
  // In-App PiP stream distribution
  const primaryStream = hasRemote
    ? (swappedStreams ? localStream : remoteStream)
    : localStream;

  const primaryLabel = hasRemote
    ? (swappedStreams ? `Вы ${muted ? '(без звука)' : ''}` : remoteName)
    : `Вы ${muted ? '(без звука)' : ''}`;

  const primaryMuted = hasRemote
    ? (swappedStreams ? true : false)
    : true;

  const primaryMirror = hasRemote
    ? (swappedStreams ? (facingMode === 'user') : false)
    : (facingMode === 'user');

  const primaryVideoOn = hasRemote
    ? (swappedStreams ? videoOn : true)
    : videoOn;

  const pipStream = hasRemote
    ? (swappedStreams ? remoteStream : localStream)
    : null;

  const pipLabel = hasRemote
    ? (swappedStreams ? remoteName : 'Вы')
    : '';

  const pipMuted = hasRemote
    ? (swappedStreams ? false : true)
    : true;

  const pipMirror = hasRemote
    ? (swappedStreams ? false : (facingMode === 'user'))
    : false;

  const pipVideoOn = hasRemote
    ? (swappedStreams ? true : videoOn)
    : true;

  return (
    <div className="relative w-full h-[100dvh] flex flex-col bg-gray-950 text-white overflow-hidden select-none">
      {/* ── Top Bar Overlay ── */}
      <div className="absolute top-0 left-0 right-0 z-30 p-3 sm:p-5 flex items-center justify-between pointer-events-none">
        {/* Left: Brand Logo, Room ID & Role & Copy Link */}
        <div className="flex items-center gap-2 sm:gap-2.5 pointer-events-auto">
          <div className="p-1 rounded-xl bg-white/10 backdrop-blur-md border border-white/10 flex items-center justify-center shadow-lg">
            <SovcombankLogo size={22} />
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide shadow-md ${
            role === 'operator' ? 'bg-[#002882] text-white' : 'bg-[#ea1c24] text-white'
          }`}>
            {ROLE_LABELS[role]}
          </span>
          <button
            type="button"
            onClick={handleCopyLink}
            title="Скопировать ссылку для входа"
            className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/60 hover:bg-black/80 text-white/90 border border-white/10 backdrop-blur-md text-xs font-mono transition-all active:scale-95 shadow-lg"
          >
            <span>{shortId(roomId)}</span>
            {copied ? <CheckIcon size={13} className="text-emerald-400" /> : <CopyIcon size={13} />}
          </button>
        </div>

        {/* Center: Live Call Duration & Recording Pill */}
        <div className="pointer-events-auto flex items-center gap-2">
          <div className="px-3.5 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-white text-xs font-mono font-medium flex items-center gap-2 shadow-lg">
            {recording && (
              <span className="flex items-center gap-1 text-red-400 font-bold uppercase tracking-wider text-[10px]">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                REC
              </span>
            )}
            {uploadingRecording && (
              <span className="flex items-center gap-1.5 text-primary-400 font-medium text-[11px]">
                <RefreshIcon size={12} className="animate-spin" />
                <span className="hidden xs:inline">Сохранение…</span>
              </span>
            )}
            <span>{formatDuration(callDuration)}</span>
          </div>
        </div>

        {/* Right: Status badge & Fullscreen / Delete */}
        <div className="flex items-center gap-2 pointer-events-auto">
          <div className="hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-xs text-white/80 shadow-lg">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>{wsConnected ? 'В сети' : 'Подключение…'}</span>
          </div>

          <button
            type="button"
            onClick={handleToggleFullscreen}
            title={isFullscreen ? 'Свернуть' : 'Во весь экран'}
            className="hidden sm:flex p-2 rounded-full bg-black/60 hover:bg-black/80 text-white border border-white/10 backdrop-blur-md transition-all active:scale-95 shadow-lg"
          >
            {isFullscreen ? <MinimizeIcon size={16} /> : <MaximizeIcon size={16} />}
          </button>

          {role === 'operator' && (
            <button
              type="button"
              onClick={handleDeleteRoom}
              disabled={deleting}
              title="Удалить комнату"
              className="p-2 rounded-full bg-black/60 hover:bg-red-900/60 text-red-400 hover:text-red-300 border border-white/10 backdrop-blur-md transition-all active:scale-95 shadow-lg"
            >
              <TrashIcon size={16} />
            </button>
          )}
        </div>
      </div>

      {/* ── Main Video Area ── */}
      <div className="relative w-full h-full flex-1 flex flex-col items-center justify-center p-2 sm:p-4 pb-20 sm:pb-24">
        {/* Stage Mode (Default for Mobile & 1-on-1 calls) */}
        {layoutMode === 'stage' || !hasRemote ? (
          <div className="relative w-full h-full flex items-center justify-center max-w-7xl mx-auto">
            {/* Main Stage Video Window */}
            <div className="w-full h-full flex items-center justify-center">
              <VideoWindow
                ref={swappedStreams ? null : remoteVideoRef}
                stream={primaryStream}
                label={primaryLabel}
                muted={primaryMuted}
                mirror={primaryMirror}
                videoOn={primaryVideoOn}
                aspect="fill"
                onPip={hasRemote && !swappedStreams ? handleTogglePip : undefined}
                onToggleFullscreen={handleToggleFullscreen}
                isFullscreen={isFullscreen}
                className="w-full h-full max-h-[calc(100dvh-90px)] sm:max-h-[calc(100dvh-120px)] object-cover rounded-2xl sm:rounded-3xl shadow-2xl"
              />
            </div>

            {/* In-App Floating PiP (Self-view or Swapped Remote) */}
            {hasRemote && pipStream && (
              <div
                className="absolute bottom-4 sm:bottom-6 right-3 sm:right-6 w-28 sm:w-44 md:w-52 aspect-[3/4] sm:aspect-video z-20"
                title="Нажмите, чтобы переключить вид"
              >
                <VideoWindow
                  ref={swappedStreams ? remoteVideoRef : null}
                  stream={pipStream}
                  label={pipLabel}
                  muted={pipMuted}
                  mirror={pipMirror}
                  videoOn={pipVideoOn}
                  isPip={true}
                  aspect="pip"
                  onSwap={() => setSwappedStreams((s) => !s)}
                  className="w-full h-full"
                />
              </div>
            )}

            {/* Waiting for participant banner if nobody joined yet */}
            {!hasRemote && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-4">
                <div className="p-5 sm:p-7 rounded-3xl bg-black/75 backdrop-blur-xl border border-white/15 text-center max-w-sm pointer-events-auto shadow-2xl space-y-3 animate-fade-in">
                  <div className="w-14 h-14 rounded-2xl bg-white/10 text-white flex items-center justify-center mx-auto ring-4 ring-white/10 p-2 shadow-inner">
                    <SovcombankLogo size={38} />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold">Ожидаем клиента</h3>
                  <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                    Отправьте прямую ссылку клиенту для защищенного подключения к консультации.
                  </p>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="btn-primary w-full justify-center !py-2.5 text-xs !rounded-xl mt-2 !bg-[#002882] hover:!bg-[#001f66] shadow-md shadow-[#002882]/30"
                  >
                    {copied ? <CheckIcon size={14} className="text-emerald-400" /> : <CopyIcon size={14} />}
                    {copied ? 'Ссылка скопирована!' : 'Скопировать ссылку для клиента'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Grid Mode (Equal tiles for desktop/multiview) */
          <div className="w-full h-full flex-1 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 max-w-6xl mx-auto items-center p-2">
            {localStream && (
              <VideoWindow
                stream={localStream}
                label={`Вы ${muted ? '(без звука)' : ''}`}
                muted
                mirror={facingMode === 'user'}
                videoOn={videoOn}
                aspect="default"
                className="w-full h-full max-h-[calc(50dvh-60px)] md:max-h-[calc(100dvh-140px)]"
              />
            )}
            {remoteArray.map(([userId, rs]) => {
              const p = participants.get(userId);
              return (
                <VideoWindow
                  key={userId}
                  ref={remoteVideoRef}
                  stream={rs}
                  label={p?.name || 'Собеседник'}
                  muted={false}
                  mirror={false}
                  videoOn={true}
                  aspect="default"
                  onPip={handleTogglePip}
                  onToggleFullscreen={handleToggleFullscreen}
                  isFullscreen={isFullscreen}
                  className="w-full h-full max-h-[calc(50dvh-60px)] md:max-h-[calc(100dvh-140px)]"
                />
              );
            })}
          </div>
        )}
      </div>

      {/* ── Bottom Controls Bar ── */}
      <CallControls
        muted={muted}
        videoOn={videoOn}
        role={role}
        onToggleMic={handleMic}
        onToggleVideo={handleVideo}
        onSwitchCamera={switchCamera}
        hasMultipleCameras={hasMultipleCameras}
        onTogglePip={hasRemote && isPipSupported ? handleTogglePip : undefined}
        isPipActive={isPipActive}
        isPipSupported={hasRemote && isPipSupported}
        layoutMode={layoutMode}
        onToggleLayout={() => setLayoutMode((m) => (m === 'stage' ? 'grid' : 'stage'))}
        recording={recording}
        onToggleRecording={handleRecording}
        onLeave={handleLeave}
        className="absolute bottom-3 sm:bottom-6 left-0 right-0 z-30 pointer-events-auto"
      />

      {/* ── Saving Recording Modal Overlay ── */}
      {uploadingRecording && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md animate-fade-in p-4 pointer-events-auto">
          <div className="p-6 sm:p-8 rounded-3xl bg-slate-900/95 border border-white/15 text-center max-w-sm space-y-4 shadow-2xl">
            <div className="w-14 h-14 mx-auto rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center animate-spin">
              <RefreshIcon size={26} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Сохранение записи</h3>
              <p className="text-xs sm:text-sm text-slate-300 mt-1.5 leading-relaxed">
                Видео сохраняется и обрабатывается на сервере (синхронизация, таймкоды, индексация)…
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * WaitingView with polished animations and mobile styling
 * @param {object} props
 * @param {string} props.roomId
 * @param {'operator'|'client'} props.role
 */
function WaitingView({ roomId, role }) {
  const navigate = useNavigate();
  const notify = useNotifyStore((s) => s.notify);
  const [room, setRoom] = useState(/** @type {import('../api.js').Room|null} */ (null));
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await api.waitForCallToStart(roomId, (r) => !cancelled && setRoom(r), 300_000);
        if (!cancelled) {
          setRoom(result);
          if (result.status === 'active') {
            notify('info', 'Оператор начал звонок');
          } else {
            notify('info', `Звонок ${result.status === 'ended' ? 'завершён' : 'истёк'}`);
            navigate('/rooms');
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          notify('error', err.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [roomId, navigate, notify]);

  const link = `${window.location.origin}/#/room/${roomId}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      notify('success', 'Ссылка скопирована');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      notify('info', link);
    });
  };

  return (
    <div className="flex-1 min-h-[100dvh] flex flex-col items-center justify-center p-4 bg-gray-950 text-white animate-fade-in">
      <div className="p-6 sm:p-8 max-w-md w-full text-center space-y-5 rounded-3xl bg-gray-900/80 border border-white/10 shadow-2xl backdrop-blur-xl">
        <div className="w-16 h-16 rounded-full bg-primary-600/20 text-primary-400 flex items-center justify-center mx-auto ring-8 ring-primary-500/10">
          <UsersIcon size={32} />
        </div>
        <div>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold tracking-wide bg-amber-500/20 text-amber-300 border border-amber-500/30 mb-2">
            Ожидание звонка
          </span>
          <h3 className="text-xl font-bold">
            Ожидание {role === 'client' ? 'оператора' : 'клиента'}
          </h3>
          <p className="text-sm text-gray-400 mt-2 leading-relaxed">
            {role === 'client'
              ? 'Оператор скоро подключится. Страница обновится автоматически.'
              : 'Отправьте ссылку клиенту для подключения к комнате.'}
          </p>
        </div>

        <div className="text-xs text-gray-300 font-mono break-all bg-black/50 rounded-xl p-3 border border-white/10 select-all">
          {link}
        </div>

        <button
          type="button"
          className="btn-primary w-full justify-center !rounded-full !py-2.5"
          onClick={handleCopy}
        >
          {copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
          {copied ? 'Ссылка скопирована!' : 'Скопировать ссылку'}
        </button>
      </div>
      {error && <p className="text-sm text-red-400 mt-4 max-w-md text-center">{error}</p>}
    </div>
  );
}

/**
 * JoinView with pre-call camera preview and device testing
 * @param {object} props
 * @param {string} props.roomId
 * @param {'operator'|'client'} props.role
 */
function JoinView({ roomId, role }) {
  const navigate = useNavigate();
  const notify = useNotifyStore((s) => s.notify);
  const callState = useCallStore((s) => s.state);
  const setRole = useCallStore((s) => s.setRole);
  const startAcquiring = useCallStore((s) => s.startAcquiring);

  const [name, setName] = useState('');
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setRole(role);
  }, [role, setRole]);

  useEffect(() => {
    if (callState === CALL_STATES.CONNECTED) {
      navigate('/room/' + roomId);
    }
  }, [callState, navigate, roomId]);

  const handleJoin = useCallback(async () => {
    setJoining(true);
    setError(null);
    try {
      const opts = role === 'client'
        ? { name: name.trim() || 'Клиент', sessionId: sessionId() }
        : { sessionId: sessionId() };
      const result = await (role === 'client' ? api.clientJoin(roomId, opts) : api.operatorJoin(roomId, opts));
      startAcquiring(result.room);
    } catch (err) {
      setError(err.message);
      notify('error', err.message);
    } finally {
      setJoining(false);
    }
  }, [roomId, role, name, notify, startAcquiring]);

  return (
    <div className="flex-1 min-h-[100dvh] flex flex-col items-center justify-center p-4 bg-gray-950 text-white animate-fade-in">
      <div className="p-6 sm:p-8 max-w-md w-full space-y-5 rounded-3xl bg-gray-900/80 border border-white/10 shadow-2xl backdrop-blur-xl">
        <div className="flex items-center justify-between">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            role === 'operator' ? 'bg-primary-600/90 text-white' : 'bg-emerald-600/90 text-white'
          }`}>
            {ROLE_LABELS[role]}
          </span>
          <span className="text-xs text-gray-400 font-mono">
            Комната: {shortId(roomId)}
          </span>
        </div>

        <div>
          <h3 className="text-xl font-bold">Подключение к звонку</h3>
          <p className="text-xs sm:text-sm text-gray-400 mt-1 leading-relaxed">
            {role === 'client'
              ? 'Представьтесь перед началом звонка, чтобы оператор вас узнал.'
              : 'Вы входите как оператор. Камера и микрофон готовы к звонку.'}
          </p>
        </div>

        {role === 'client' && (
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Ваше имя
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: Алексей"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 transition-colors"
              onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
              autoFocus
            />
          </div>
        )}

        <button
          type="button"
          className="btn-primary w-full justify-center !rounded-xl !py-3 font-semibold shadow-lg shadow-primary-600/30 active:scale-[0.98] transition-all"
          onClick={handleJoin}
          disabled={joining || (role === 'client' && !name.trim())}
        >
          {joining ? 'Подключение…' : 'Войти в комнату'}
        </button>

        {error && <p className="text-sm text-red-400 text-center">{error}</p>}
      </div>
    </div>
  );
}

export default function Room() {
  const { id: roomId } = useParams();
  const [searchParams] = useSearchParams();
  const role = (searchParams.get('role') || 'client').slice(0, 8);
  const mode = searchParams.get('mode');

  if (!roomId) return <ErrorView title="Комната не указана" onRetry={() => window.history.back()} />;
  if (mode === 'join') return <JoinView roomId={roomId} role={role} />;
  if (mode === 'waiting') return <WaitingView roomId={roomId} role={role} />;
  return <CallView roomId={roomId} role={role} />;
}
